import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Image as ImageIcon, Facebook, ExternalLink, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import axios from 'axios';
import SEO from '../components/SEO';
import { seoConfig } from '../config/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GalleryPage = () => {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [imagesRes, albumsRes] = await Promise.allSettled([
          axios.get(`${BACKEND_URL}/api/gallery`),
          axios.get(`${BACKEND_URL}/api/facebook-albums`)
        ]);
        if (imagesRes.status === 'fulfilled') setImages(imagesRes.value.data.images || []);
        if (albumsRes.status === 'fulfilled') setAlbums(albumsRes.value.data.albums || []);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredImages = categoryFilter === 'all'
    ? images
    : images.filter(img => img.category === categoryFilter);

  const filteredAlbums = categoryFilter === 'all'
    ? albums
    : albums.filter(album => album.category === categoryFilter);

  const handlePrevImage = useCallback(() => {
    setSelectedImageIndex((prev) => {
      if (prev === null) return null;
      return prev > 0 ? prev - 1 : filteredImages.length - 1;
    });
  }, [filteredImages.length]);

  const handleNextImage = useCallback(() => {
    setSelectedImageIndex((prev) => {
      if (prev === null) return null;
      return prev < filteredImages.length - 1 ? prev + 1 : 0;
    });
  }, [filteredImages.length]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex === null) return;
      if (e.key === 'ArrowLeft') handlePrevImage();
      if (e.key === 'ArrowRight') handleNextImage();
      if (e.key === 'Escape') setSelectedImageIndex(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, handlePrevImage, handleNextImage]);

  const selectedImage = selectedImageIndex !== null ? filteredImages[selectedImageIndex] : null;

  return (
    <div className="min-h-screen bg-[#060606] pt-48 pb-32" data-testid="gallery-page">
      <SEO {...seoConfig.galerie} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-5xl sm:text-6xl tracking-tight text-[#F5F0E8] mb-3"
          >
            {t('gallery.title')}
          </motion.h1>
          <p className="font-serif italic text-lg text-[#B9B2A8]">
            {t('gallery.subtitle')}
          </p>
        </div>

        {/* Filters */}
        <div className="flex justify-center mb-10">
          <Tabs defaultValue="all" onValueChange={setCategoryFilter}>
            <TabsList className="bg-white/5 border border-white/10">
              <TabsTrigger
                value="all"
                className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#060606]"
              >
                {t('gallery.all')}
              </TabsTrigger>
              <TabsTrigger
                value="event"
                className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#060606]"
              >
                {t('gallery.events')}
              </TabsTrigger>
              <TabsTrigger
                value="crowd"
                className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#060606]"
              >
                {t('gallery.crowd')}
              </TabsTrigger>
              <TabsTrigger
                value="venue"
                className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#060606]"
              >
                {t('gallery.venue')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[#B9B2A8]">{t('history.loading')}</p>
          </div>
        )}

        {/* Facebook Albums Section */}
        {!loading && filteredAlbums.length > 0 && (
          <div className="mb-16">
            <h2 className="font-display text-2xl text-[#F5F0E8] mb-6 flex items-center gap-2">
              <Facebook className="w-6 h-6 text-[#C9A84C]" />
              {t('gallery.albums')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAlbums.map((album, index) => (
                <motion.div
                  key={album.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/5 border border-white/10 rounded-2xl p-6 card-hover group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="font-display text-lg text-[#F5F0E8]">
                      {album.title}
                    </h3>
                    <Facebook className="w-5 h-5 text-[#C9A84C]" />
                  </div>
                  <p className="text-sm text-[#B9B2A8] mb-4">
                    {t('gallery.category')}: {album.category}
                  </p>
                  <a
                    href={album.album_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full"
                  >
                    <Button
                      className="w-full bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A] flex items-center justify-center gap-2"
                    >
                      {t('gallery.view_album')} <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Images Grid */}
        {!loading && filteredImages.length > 0 && (
          <div>
            <h2 className="font-display text-2xl text-[#F5F0E8] mb-6 flex items-center gap-2">
              <ImageIcon className="w-6 h-6 text-[#C9A84C]" />
              {t('gallery.photos')}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredImages.map((image, index) => (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="relative group aspect-square rounded-xl overflow-hidden bg-white/5 border border-white/10 card-hover cursor-pointer"
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <img
                    src={image.url}
                    alt={image.title || 'Gallery image'}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                  />
                  {image.title && (
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                      <p className="text-xs text-white">{image.title}</p>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ExternalLink className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredImages.length === 0 && filteredAlbums.length === 0 && (
          <div className="text-center py-20">
            <ImageIcon className="w-16 h-16 mx-auto mb-4 text-[#B9B2A8] opacity-50" />
            <p className="text-[#B9B2A8] text-lg">
              {t('gallery.empty')}
            </p>
          </div>
        )}
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={selectedImageIndex !== null} onOpenChange={(open) => { if (!open) setSelectedImageIndex(null); }}>
        <DialogContent className="max-w-5xl bg-[#0B0B0C]/95 border-white/10 p-2 sm:p-4">
          {selectedImage && (
            <div className="relative">
              {/* Image */}
              <div className="relative flex items-center justify-center min-h-[300px] max-h-[80vh]">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.title || 'Gallery image'}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />
              </div>

              {/* Title overlay */}
              {selectedImage.title && (
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent rounded-b-lg">
                  <p className="text-sm text-[#F5F0E8] text-center">{selectedImage.title}</p>
                </div>
              )}

              {/* Navigation: Previous */}
              {filteredImages.length > 1 && (
                <button
                  onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 border border-white/10 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}

              {/* Navigation: Next */}
              {filteredImages.length > 1 && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 border border-white/10 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}

              {/* Counter */}
              {filteredImages.length > 1 && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-black/50 px-3 py-1 rounded-full text-xs text-[#B9B2A8]">
                  {selectedImageIndex + 1} / {filteredImages.length}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
